/**
 * Dialog to manage custom field definitions at project or list scope.
 * Create, and delete (archive when values exist is backend-handled; delete when no values).
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useProjectCustomFields,
  useListCustomFields,
  useCreateProjectCustomField,
  useCreateListCustomField,
  useDeleteCustomField,
} from '@/hooks/use-custom-fields';
import { toast } from '@/components/ui/toaster';
import type { CustomFieldType } from '@/lib/api';

const FIELD_TYPES: { value: CustomFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Select' },
  { value: 'checkbox', label: 'Checkbox' },
];

interface ManageCustomFieldsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: 'project' | 'list';
  projectId?: string;
  listId?: string;
  title: string;
}

export function ManageCustomFieldsDialog({
  open,
  onOpenChange,
  scope,
  projectId,
  listId,
  title,
}: ManageCustomFieldsDialogProps) {
  const [newName, setNewName] = useState('');
  const [newKey, setNewKey] = useState('');
  const [newType, setNewType] = useState<CustomFieldType>('text');

  const projectFields = useProjectCustomFields(scope === 'project' ? projectId : undefined, { queryEnabled: open });
  const listFields = useListCustomFields(scope === 'list' ? listId : undefined, { queryEnabled: open });
  const createProjectField = useCreateProjectCustomField(projectId ?? '');
  const createListField = useCreateListCustomField(listId ?? '');
  const deleteField = useDeleteCustomField();

  const activeQuery = scope === 'project' ? projectFields : listFields;
  const fields = scope === 'project' ? projectFields.data ?? [] : listFields.data ?? [];
  const createField = scope === 'project' ? createProjectField : createListField;

  const handleAdd = async () => {
    const name = newName.trim();
    const key = newKey.trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
    if (!name || !key) {
      toast({ title: 'Name and key are required', variant: 'destructive' });
      return;
    }
    if (scope === 'project' && !projectId) return;
    if (scope === 'list' && !listId) return;
    try {
      if (scope === 'project') {
        await createProjectField.mutateAsync({ name, key, type: newType });
      } else {
        await createListField.mutateAsync({ name, key, type: newType });
      }
      toast({ title: 'Field created' });
      setNewName('');
      setNewKey('');
      setNewType('text');
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message: string }).message) : 'Failed to create field';
      toast({ title: msg, variant: 'destructive' });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteField.mutateAsync(id);
      toast({ title: 'Field removed' });
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message: string }).message) : 'Cannot delete';
      toast({ title: msg, variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#1a1a1a] border-[#444]">
        <DialogHeader>
          <DialogTitle>Manage custom fields</DialogTitle>
          <DialogDescription>{title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Add field</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Field name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="bg-[#2a2a2a] border-[#444]"
              />
              <Input
                placeholder="key_name"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                className="bg-[#2a2a2a] border-[#444] w-28"
              />
              <Select value={newType} onValueChange={(v) => setNewType(v as CustomFieldType)}>
                <SelectTrigger className="w-28 bg-[#2a2a2a] border-[#444]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={handleAdd}
                disabled={createField.isPending || activeQuery.isLoading || !newName.trim() || !newKey.trim()}
              >
                Add
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-muted-foreground text-xs">Current fields</Label>
            <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
              {open && activeQuery.isLoading ? (
                <li className="text-sm text-muted-foreground py-2">Loading fields…</li>
              ) : open && activeQuery.isError ? (
                <li className="text-sm text-destructive py-2">Could not load fields. Try again.</li>
              ) : fields.length === 0 ? (
                <li className="text-sm text-muted-foreground py-2">No custom fields yet</li>
              ) : (
                fields.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-center justify-between py-1.5 px-2 rounded bg-[#2a2a2a] border border-[#444]"
                  >
                    <span className="text-sm truncate">
                      {f.name} <span className="text-muted-foreground">({f.key})</span> — {f.type}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(f.id)}
                      disabled={deleteField.isPending}
                    >
                      Remove
                    </Button>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
